import pick from 'lodash/pick';

import {ChartType, PropertyType, ResourceType} from 'common/report/constants';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {INSIGHTS_GROUP_PROPERTIES} from 'common/report/insights/models/constants';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {getEventValueAndLabel} from 'common/report/util/events';
import {assert} from 'common/util';
import {assertUnreachable} from 'common/util/typescript';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FiltersOperator, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {TrackName as DataTableTrackName} from 'common/components/data-table/types';
import {FilterDeterminer} from 'common/report/insights/util/types';
import {parseNumber, parseNumberRange} from 'common/report/util/sorting/header-sorting';
import {GroupClause} from 'common/report/insights/models/group-clause';
import {getCalendarDate, parseDatetimeRange} from 'common/util/date';
import {INSIGHTS_DATE_RANGE_NEGATION_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {
  BOOLEAN_OPERATOR_NEGATION_MAP,
  NUMBER_OPERATOR_NEGATION_MAP,
  STRING_OPERATOR_NEGATION_MAP,
} from 'common/report/insights/util';
import {
  BooleanOperator,
  ListQuantifier,
  NumberOperator,
  StringOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {ConstantPropertyValues} from 'common/report/util/properties';
import {MetricType} from 'common/query-builder/query-block/constants';
import {isMultiAttributionBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {BEHAVIOR_MULTI_ATTRIBUTION_FILTER} from 'common/query-builder/query-block/properties-menu/constants';

function determinerToFiltersOperator(determiner: FilterDeterminer) {
  if (!determiner) {
    return null;
  }
  switch (determiner) {
    case `any`:
      return FiltersOperator.Or;
    case `all`:
      return FiltersOperator.And;
    default:
      assertUnreachable(determiner);
  }
}

export function convertSegmentForEventsInspector(behaviorShowClauses: BehaviorShowClause[]): QueryEventEntry[] {
  return behaviorShowClauses.map((showClause) => {
    const showClauseFilters = showClause.behavior.filters || [];
    const determiner = showClauseFilters.find((filter: FilterClause) => filter.determiner)?.determiner;
    const filters: PropertyFilter[] = showClauseFilters.map(
      (filter: FilterClause): PropertyFilter => {
        return new FilterClause(filter).toPropertyFilter();
      },
    );
    const {name, id} = showClause.behavior;
    const custom = showClause.behavior.type === MetricType.CustomEvent;
    assert(!Array.isArray(name));
    const {label, value} = getEventValueAndLabel({name, custom, id});
    const queryEventEntry: QueryEventEntry = {
      type: ResourceType.Event,
      dataGroupId: showClause.behavior.dataGroupId,
      event: {
        label,
        value,
        custom,
        id,
      },
      filters,
      filtersOperator: determinerToFiltersOperator(determiner),
    };
    return queryEventEntry;
  });
}

export function hasDataTable(chartType: ChartType) {
  return [ChartType.Line, ChartType.Metric, ChartType.Pie, ChartType.FunnelSteps].includes(chartType);
}

export function getDataTableTrackName(chartType: ChartType) {
  switch (chartType) {
    case ChartType.Line:
      return DataTableTrackName.InsightsLine;
    case ChartType.Metric:
      return DataTableTrackName.InsightsMetric;
    case ChartType.Pie:
      return DataTableTrackName.InsightsPie;
    case ChartType.FunnelSteps:
      return DataTableTrackName.FunnelsSteps;
    default:
      throw new Error(`Data table not supported for ${chartType}`);
  }
}

export function isNoneBucketing(groupClause) {
  if (groupClause.customBucket && `disabled` in groupClause.customBucket) {
    return groupClause.customBucket.disabled === true ? PropertyType.String : false;
  }

  return false;
}

export function createFilterFromGroup(
  groupClause: Partial<GroupClause>,
  filterValue,
  includesFilterValue: boolean,
  timePeriodSettings,
) {
  const groupDataset = groupClause.dataset;
  const groupProperties = pick(groupClause, INSIGHTS_GROUP_PROPERTIES);
  groupProperties[`defaultType`] = groupClause.propertyType;
  if (groupClause.propertyFilter && groupClause.propertyFilter.propertyType !== groupClause[`propertyDefaultType`]) {
    groupProperties.typeCast = groupClause.propertyFilter.propertyType;
  }
  const joinType = groupClause.joinPropertyType;
  if (joinType) {
    groupProperties.filterJoinType = joinType;

    if (joinType === PropertyType.List) {
      groupProperties.listQuantifier = !includesFilterValue
        ? ListQuantifier.All
        : groupClause?.listQuantifier ?? ListQuantifier.Any;
    }
  }

  if (isMultiAttributionBehavior(groupClause.behavior)) {
    return createMultiAttributionFilter(groupClause, filterValue, includesFilterValue);
  }

  // None bucketing must evaluate to a String property type
  const groupType =
    isNoneBucketing(groupClause) ||
    groupProperties.typeCast ||
    groupProperties.propertyType ||
    groupProperties.customProperty?.propertyType;

  if ([`undefined`].includes(filterValue)) {
    if (groupType === `number`) {
      return createUndefinedFilter(groupDataset, groupProperties, includesFilterValue, `number`);
    }

    return createStringFilter(groupDataset, groupProperties, `undefined`, includesFilterValue, `string`);
  }

  switch (groupType) {
    case `string`:
      return createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
    case `boolean`:
      return createBooleanFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
    case `list`:
      // We cannot use list selectors to filter out empty lists, since they apply element-wise
      // Cast list property to string and do the comparison with $empty_list
      // query-api correctly resolves $empty_list to the right value
      if (filterValue === ConstantPropertyValues.EmptyList) {
        return createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      } else {
        return createListFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      }
    case `number`:
      return createNumberFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
    case `object`:
      if (groupClause.listItemGroup) {
        return createListOfObjectsFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
      }

      break;
    case `datetime`:
      if ([`day_of_week`, `hour_of_day`].includes(groupProperties.unit)) {
        const filter = createStringFilter(groupDataset, groupProperties, filterValue, includesFilterValue, groupClause);
        filter[`filterDateUnit`] = groupProperties.unit;
        return filter;
      } else {
        return createDatetimeFilter(
          groupDataset,
          groupProperties,
          filterValue,
          timePeriodSettings,
          includesFilterValue,
          groupClause,
        );
      }
  }

  return null;
}

export function createUndefinedFilter(groupDataset, groupProperties, includesFilterValue, filterType = `string`) {
  return {
    ...groupProperties,
    filterOperator: includesFilterValue ? `is not set` : `is set`,
    filterType,
    dataset: groupDataset,
  };
}

export function createStringFilter(
  groupDataset,
  groupProperties,
  filterValue,
  includesFilterValue,
  groupClause = null,
) {
  let filterOperator = includesFilterValue ? `equals` : `does not equal`;

  if (groupClause?.filterOperator) {
    filterOperator = includesFilterValue
      ? groupClause.filterOperator
      : STRING_OPERATOR_NEGATION_MAP[groupClause.filterOperator];
  }

  return {
    ...groupProperties,
    filterOperator,
    filterType: `string`,
    filterValue: groupClause?.filterValue ?? [filterValue],
    dataset: groupDataset,
  };
}

export function createMultiAttributionFilter(
  groupClause: Partial<GroupClause>,
  filterValue: string,
  includesFilterValue: boolean,
) {
  return {
    ...groupClause,
    value: BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value,
    filterJoinType: PropertyType.List,
    filterOperator: includesFilterValue ? StringOperator.Equals : StringOperator.DoesNotEqual,
    filterType: PropertyType.String,
    filterValue: Array.isArray(filterValue) ? [...filterValue] : [filterValue],
    listQuantifier: ListQuantifier.Any,
  };
}

export function createBooleanFilter(
  groupDataset,
  groupProperties,
  filterValue,
  includesFilterValue,
  groupClause = null,
) {
  // Value Comparison case
  if (groupClause?.filterOperator) {
    return {
      ...groupProperties,
      filterOperator: includesFilterValue
        ? groupClause.filterOperator
        : BOOLEAN_OPERATOR_NEGATION_MAP[groupClause.filterOperator],
      filterType: `boolean`,
      dataset: groupDataset,
    };
  }

  const isTrue = !!JSON.parse(filterValue) === includesFilterValue;
  return {
    ...groupProperties,
    filterOperator: isTrue ? `is true` : `is false`,
    filterType: `boolean`,
    dataset: groupDataset,
  };
}

export function createListFilter(groupDataset, groupProperties, filterValue, includesFilterValue, _groupClause = null) {
  const filter = {
    ...groupProperties,
    dataset: groupDataset,
    filterValue,
    filterType: `list`,
    filterOperator: includesFilterValue ? `contains` : `does not contain`,
  };

  return filter;
}

export function createNumberFilter(groupDataset, groupProperties, value, includesFilterValue, groupClause = null) {
  // Value Comparison case
  if (groupClause?.filterOperator && (groupClause?.filterValue || groupClause?.filterValue === 0)) {
    return {
      ...groupProperties,
      filterType: `number`,
      dataset: groupDataset,
      filterOperator: includesFilterValue
        ? groupClause.filterOperator
        : NUMBER_OPERATOR_NEGATION_MAP[groupClause.filterOperator],
      filterValue: groupClause.filterValue,
    };
  }

  const {filterOperator, filterValue} = assignNumericFilterOperatorAndValue(value, includesFilterValue);

  return {
    ...groupProperties,
    filterType: `number`,
    dataset: groupDataset,
    filterOperator,
    filterValue,
  };
}

export function createListOfObjectsFilter(
  groupDataset,
  groupProperties,
  filterValue,
  includesFilterValue,
  groupClause,
) {
  // group is a list of objects property breakdown
  const listItemFilter = createFilterFromGroup(
    new GroupClause({
      ...groupClause.listItemGroup,
      resourceType: groupClause.resourceType,
      value: groupClause.listItemGroup.propertyName,
    }),
    filterValue,
    includesFilterValue,
    `a`,
  );

  // list of objects filter is most like a boolean filter
  const filter = createBooleanFilter(groupDataset, groupProperties, true, true, groupClause);
  filter.filterType = PropertyType.Object;
  filter.filterOperator = BooleanOperator.True;
  filter.listItemFilters = [listItemFilter];
  filter.listQuantifier = includesFilterValue ? ListQuantifier.Any : ListQuantifier.All;

  return filter;
}

export function assignNumericFilterOperatorAndValue(value, includesFilterValue) {
  const numericValue = typeof value === `number` ? value : parseNumber(value);
  const numberRange = typeof value === `number` ? value : parseNumberRange(value);
  let filterOperator;
  let filterValue;

  if (!Array.isArray(numberRange) && numericValue === null) {
    return null;
  }

  if (numericValue !== null) {
    filterOperator = includesFilterValue ? NumberOperator.EqualTo : NumberOperator.NotEqualTo;
    filterValue = numericValue;
  } else {
    if (Number.isFinite(numberRange[0]) && Number.isFinite(numberRange[1])) {
      filterOperator = includesFilterValue ? NumberOperator.Between : NumberOperator.NotBetween;
      filterValue = numberRange;
    } else if (Number.isFinite(numberRange[1])) {
      filterOperator = includesFilterValue ? NumberOperator.LessThan : NumberOperator.AtLeast;
      filterValue = numberRange[1];
    } else {
      filterOperator = includesFilterValue ? NumberOperator.AtLeast : NumberOperator.LessThan;
      filterValue = numberRange[0];
    }
  }

  return {filterOperator, filterValue};
}

export function createDatetimeFilter(
  groupDataset,
  groupProperties,
  filterValue,
  timePeriodSettings,
  includesFilterValue,
  groupClause = null,
) {
  const unit = groupProperties.unit;
  const quarterStartMonth =
    timePeriodSettings && timePeriodSettings.quarter_start_month ? timePeriodSettings.quarter_start_month : 0;
  let dateRange = parseDatetimeRange(filterValue, unit, quarterStartMonth);
  let defaultAndFilterType = `datetime`;
  let filterOperator;

  if (unit === TimeUnit.Hour) {
    dateRange = getCalendarDate(dateRange[0]);
    defaultAndFilterType = `string`;
  }
  if (groupClause?.filterOperator) {
    filterOperator = includesFilterValue
      ? groupClause.filterOperator
      : INSIGHTS_DATE_RANGE_NEGATION_MAPPING[groupClause.filterOperator];
  }

  if (dateRange) {
    return {
      ...groupProperties,
      defaultType: groupClause?.defaultType ?? defaultAndFilterType,
      filterOperator: filterOperator ?? parseDatetimeFilterOperator(unit, includesFilterValue),
      filterType: groupClause?.filterType ?? defaultAndFilterType,
      filterValue: groupClause?.filterValue ?? dateRange,
      filterDateUnit: unit,
      dataset: groupDataset,
    };
  }

  return null;
}

export function parseDatetimeFilterOperator(unit, includesFilterValue) {
  if (unit === `hour`) {
    return includesFilterValue ? `contains` : `does not contain`;
  } else if (unit === `day`) {
    return includesFilterValue ? `was on` : `was not on`;
  } else {
    return includesFilterValue ? `was between` : `was not between`;
  }
}
